/**
 * CSV Parser Worker Client
 * Wrapper for managing the CSV parser Web Worker
 */

import type { WorkerRequest, WorkerResponse } from '../workers/csvParser.worker'
import type { ParseResult } from './csvParser'
import type { GroupedLayer } from './layerGrouper'

export interface ParseCSVResult {
  parsedData: ParseResult
  groupedLayers: GroupedLayer[]
}

class CSVWorkerClient {
  private worker: Worker | null = null

  /**
   * Initialize the worker (lazy initialization)
   */
  private getWorker(): Worker {
    if (!this.worker) {
      // Use Vite's worker import syntax
      this.worker = new Worker(new URL('../workers/csvParser.worker.ts', import.meta.url), { type: 'module' })
    }
    return this.worker
  }

  /**
   * Parse CSV content using Web Worker
   */
  async parseCSV(content: string, excludeIsolated = false): Promise<ParseCSVResult> {
    return new Promise((resolve, reject) => {
      const worker = this.getWorker()

      // Set up one-time message handler
      const handleMessage = (event: MessageEvent<WorkerResponse>) => {
        const { type, parsedData, groupedLayers, error } = event.data

        // Clean up
        worker.removeEventListener('message', handleMessage)
        worker.removeEventListener('error', handleError)

        if (type === 'success' && parsedData && groupedLayers) {
          resolve({ parsedData, groupedLayers })
        } else if (type === 'error' || error) {
          reject(new Error(error || 'Unknown error'))
        } else {
          reject(new Error('Invalid response from worker'))
        }
      }

      const handleError = (error: ErrorEvent) => {
        worker.removeEventListener('message', handleMessage)
        worker.removeEventListener('error', handleError)
        reject(new Error(`Worker error: ${error.message}`))
      }

      worker.addEventListener('message', handleMessage)
      worker.addEventListener('error', handleError)

      // Send parse request
      const request: WorkerRequest = {
        type: 'parse',
        content,
        excludeIsolated,
      }
      worker.postMessage(request)
    })
  }

  /**
   * Terminate the worker to free up resources
   */
  terminate() {
    if (this.worker) {
      this.worker.terminate()
      this.worker = null
    }
  }
}

// Export singleton instance
export const csvWorkerClient = new CSVWorkerClient()
